import React, { useState } from "react";
import {
  Modal,
  Table,
  Button,
  Form,
  Input,
  Popconfirm,
  message,
  Space,
  Divider,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SaveOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import {
  useBrandsQuery,
  useCreateBrandMutation,
  useUpdateBrandMutation,
  useDeleteBrandMutation,
} from "../../Services";
import { Brand } from "../../Types";
import { modalFooter, modalTitle } from "../AppUi";

interface BrandModalProps {
  open: boolean;
  onClose: () => void;
}

export const BrandModal: React.FC<BrandModalProps> = ({ open, onClose }) => {
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState<number | null>(null);

  const { data: brands, isLoading } = useBrandsQuery({
    enabled: open,
  });

  const createMutation = useCreateBrandMutation();
  const updateMutation = useUpdateBrandMutation();
  const deleteMutation = useDeleteBrandMutation();

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      createMutation.mutate(values, {
        onSuccess: (res) => {
          if (res?.success) {
            message.success("Brand added successfully!");
            form.resetFields();
          } else {
            message.error(res?.message || "Failed to add brand.");
          }
        },
        onError: () => message.error("Something went wrong."),
      });
    } catch {
      message.error("Please enter a brand name.");
    }
  };

  const handleSaveEdit = async (record: Brand, newName: string) => {
    if (!newName.trim()) {
      message.error("Brand name cannot be empty.");
      return;
    }
    updateMutation.mutate(
      { id: record.id, name: newName.trim() },
      {
        onSuccess: (res) => {
          if (res?.success) {
            message.success("Brand updated successfully!");
            setEditingId(null);
          } else {
            message.error(res?.message || "Failed to update brand.");
          }
        },
        onError: () => message.error("Something went wrong."),
      },
    );
  };

  const handleDelete = async (id: number) => {
    deleteMutation.mutate(id, {
      onSuccess: (res) => {
        if (res?.success) {
          message.success("Brand deleted successfully!");
        } else {
          message.error(res?.message || "Failed to delete brand.");
        }
      },
      onError: () => message.error("Something went wrong."),
    });
  };

  const columns = [
    {
      title: "Brand Name",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: Brand) => {
        const isEditing = record.id === editingId;
        return isEditing ? (
          <Input
            defaultValue={text}
            id={`brand-input-${record.id}`}
            size="small"
            onPressEnter={(e: any) => handleSaveEdit(record, e.target.value)}
            style={{ width: "100%" }}
          />
        ) : (
          <span className="font-medium text-gray-700">{text}</span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_: any, record: Brand) => {
        const isEditing = record.id === editingId;
        return isEditing ? (
          <Space size="middle">
            <Button
              type="text"
              icon={<SaveOutlined className="text-green-600" />}
              size="small"
              onClick={() => {
                const input = document.getElementById(
                  `brand-input-${record.id}`,
                ) as HTMLInputElement;
                if (input) handleSaveEdit(record, input.value);
              }}
            />
            <Button
              type="text"
              icon={<CloseOutlined className="text-gray-400" />}
              size="small"
              onClick={() => setEditingId(null)}
            />
          </Space>
        ) : (
          <Space size="middle">
            <Button
              type="text"
              icon={<EditOutlined />}
              size="small"
              onClick={() => setEditingId(record.id)}
            />
            <Popconfirm
              title="Are you sure you want to delete this brand?"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <Modal
      title={modalTitle("Manage Brands")}
      open={open}
      onCancel={onClose}
      footer={modalFooter({
        actions: [{ label: "Close", onClick: onClose }],
      })}
      width={500}
    >
      <Form
        form={form}
        layout="inline"
        className="my-4 flex items-center justify-between"
      >
        <Form.Item
          name="name"
          rules={[{ required: true, message: "Brand name is required" }]}
          style={{ flex: 1, marginRight: 8 }}
        >
          <Input
            placeholder="e.g. Coca-Cola, Samsung"
            prefix={<PlusOutlined />}
          />
        </Form.Item>
        <Form.Item style={{ marginRight: 0 }}>
          <Button
            type="primary"
            size="middle"
            onClick={handleCreate}
            loading={createMutation.isPending}
          >
            Add Brand
          </Button>
        </Form.Item>
      </Form>

      <Divider style={{ margin: "12px 0" }} />

      <Table
        dataSource={brands}
        columns={columns}
        rowKey="id"
        size="small"
        loading={isLoading}
        pagination={{ pageSize: 5, size: "small", showSizeChanger: false }}
        bordered
      />
    </Modal>
  );
};
